import type { WithId } from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../utils/date';
import { formatVitalValue } from '../../utils/observation';
import { Badge } from '../ui/badge';
import { Item, ItemContent } from '../ui/item';

export type VitalDetailProps = {
  vitalName: string;
  results: WithId<Observation>[];
};

export const VitalDetail = ({ vitalName, results }: VitalDetailProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const latest = results[0];
  const resultCount = results.length;

  if (!latest) {
    return (
      <div className="mx-auto w-full max-w-4xl p-5">
        <p className="py-8 text-center text-sm text-muted-foreground">{t('noVitalsFound')}</p>
      </div>
    );
  }

  const formatValue = (obs: Observation): string => formatVitalValue(obs, t('unknown'));

  const value = formatValue(latest);

  return (
    <div className="mx-auto w-full max-w-4xl p-5">
      <div className="mb-3">
        <h1 className="text-sm font-medium uppercase">{vitalName}</h1>
        <p className="text-xs text-muted-foreground">{t('resultCountOnRecord', { count: resultCount })}</p>
      </div>

      <div className="mb-3">
        <Item size="sm" className="bg-muted/50">
          <ItemContent>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">
                {t('latestResult')}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {t('mostRecentLabel')} {formatDate(latest.effectiveDateTime)}
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-semibold">{value}</span>
            </div>
            <div className="grid grid-cols-2 gap-x-4 text-xs">
              <div>
                <span className="text-muted-foreground">{t('status')}</span> {latest.status ?? t('unknown')}
              </div>
              {latest.effectiveDateTime && (
                <div>
                  <span className="text-muted-foreground">{t('collected')}</span> {formatDate(latest.effectiveDateTime)}
                </div>
              )}
            </div>
          </ItemContent>
        </Item>
      </div>

      {results.length > 1 && (
        <div>
          <h2 className="mb-2 text-xs font-semibold text-muted-foreground">{t('resultHistory')}</h2>
          <div className="space-y-1">
            {results.slice(1).map((result, index) => {
              const resultValue = formatValue(result);
              return (
                <Item key={result.id ?? index} size="sm">
                  <ItemContent>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-xs text-muted-foreground">
                        {result.effectiveDateTime ? formatDate(result.effectiveDateTime) : t('unknown')}
                      </span>
                      <span className="text-sm font-semibold">{resultValue}</span>
                    </div>
                  </ItemContent>
                </Item>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
